import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { MailCheck } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useI18n } from "@/i18n/LanguageContext";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function ForgotPassword() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const requestCode = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/password-reset/request", { email });
      toast.success(t("reset.sent"));
      setStep(2);
    } catch (err) {
      toast.error(apiError(err));
    }
    setBusy(false);
  };

  const confirmReset = async (e) => {
    e.preventDefault();
    if (pw.length < 8) return toast.error(t("reset.tooShort"));
    if (pw !== confirm) return toast.error(t("reset.mismatch"));
    setBusy(true);
    try {
      await api.post("/auth/password-reset/confirm", { email, code: code.trim(), new_password: pw });
      toast.success(t("reset.done"));
      navigate("/login");
    } catch (err) {
      toast.error(apiError(err));
    }
    setBusy(false);
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="mx-auto max-w-md px-5 py-16 lg:py-24">
        <h1 className="font-display text-3xl tracking-tight sm:text-4xl" data-testid="forgot-title">{t("reset.title")}</h1>
        <p className="mt-3 text-base text-slate-600">{step === 1 ? t("reset.sub1") : t("reset.sub2")}</p>

        {step === 1 ? (
          <form onSubmit={requestCode} data-testid="forgot-request-form" className="mt-9 space-y-5 rounded-xl border border-slate-200 bg-white p-7 ambient">
            <div>
              <Label htmlFor="fp-email">{t("auth.email")}</Label>
              <Input id="fp-email" type="email" required autoComplete="email" data-testid="forgot-email-input" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5" />
            </div>
            <Button type="submit" disabled={busy} data-testid="forgot-send-btn" className="h-11 w-full bg-sea hover:bg-sea-dark">
              {busy ? t("common.loading") : t("reset.sendCode")}
            </Button>
            <p className="text-center text-sm text-slate-600">
              <Link to="/login" data-testid="back-to-login-link" className="font-semibold text-sea underline">{t("reset.backToLogin")}</Link>
            </p>
          </form>
        ) : (
          <form onSubmit={confirmReset} data-testid="forgot-confirm-form" className="mt-9 space-y-5 rounded-xl border border-slate-200 bg-white p-7 ambient">
            <div className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
              <MailCheck size={16} className="shrink-0" /> {t("reset.checkEmail")}
            </div>
            <div>
              <Label htmlFor="fp-code">{t("reset.code")}</Label>
              <Input id="fp-code" required inputMode="numeric" data-testid="forgot-code-input" value={code} onChange={(e) => setCode(e.target.value)} className="mt-1.5" />
            </div>
            <div>
              <Label htmlFor="fp-new">{t("reset.newPassword")}</Label>
              <Input id="fp-new" type="password" required autoComplete="new-password" data-testid="forgot-new-input" value={pw} onChange={(e) => setPw(e.target.value)} className="mt-1.5" />
            </div>
            <div>
              <Label htmlFor="fp-confirm">{t("reset.confirm")}</Label>
              <Input id="fp-confirm" type="password" required autoComplete="new-password" data-testid="forgot-confirm-input" value={confirm} onChange={(e) => setConfirm(e.target.value)} className="mt-1.5" />
            </div>
            <Button type="submit" disabled={busy} data-testid="forgot-reset-btn" className="h-11 w-full bg-sea hover:bg-sea-dark">
              {busy ? t("common.loading") : t("reset.resetBtn")}
            </Button>
            <p className="text-center text-sm text-slate-600">
              <button type="button" data-testid="forgot-resend-btn" onClick={requestCode} className="font-semibold text-sea underline">{t("reset.resend")}</button>
            </p>
          </form>
        )}
      </div>
      <Footer />
    </div>
  );
}
